import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasTableOfContents, headings, paragraphsWithStyle, readDocument } from "@/office/word/document";
import { readFields } from "@/office/word/layout";
import { readNotes } from "@/office/word/references";

export const lektionVerweiseAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Eine wissenschaftliche Arbeit",
  summary: "Verzeichnisse, Fußnoten und Verweise in einem Dokument zusammenführen.",
  durationMinutes: 12,
  learningGoals: [
    "Alle Verweistypen in einem Dokument kombinieren",
    "Verzeichnisse gemeinsam aktualisieren",
  ],
  steps: [
    {
      id: "arbeit-aufbauen",
      title: "Die Arbeit aufbauen",
      explanation: [
        "Baue das Grundgerüst einer kleinen wissenschaftlichen Arbeit: nummerierte " +
          "Kapitel, ein Inhaltsverzeichnis, mindestens eine beschriftete Abbildung, zwei " +
          "Fußnoten und einen Querverweis im Text.",
        "Wenn du alles beisammen hast, drücke Strg + A und F9. Alle Verzeichnisse und " +
          "Verweise aktualisieren sich dann in einem Zug – der Moment, in dem sich die " +
          "Arbeit der letzten Module auszahlt.",
      ],
      keyPoints: [
        "Überschriften mit Vorlagen, dann Inhaltsverzeichnis.",
        "Abbildung mit Beschriftung, dann Abbildungsverzeichnis.",
        "Fußnoten für Anmerkungen, Querverweise für Verweise im Text.",
      ],
      task: {
        instruction:
          "Dein Dokument soll enthalten: mindestens drei Überschriften, ein " +
          "Inhaltsverzeichnis, mindestens eine Beschriftung, mindestens zwei Fußnoten und " +
          "einen Querverweis.",
        hint: "Was diese Word-Version nicht prüfen kann, wird als solches gemeldet – der Rest wird bewertet.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          if (alle.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Überschriften.",
              "Fang mit der Gliederung an – darauf bauen alle Verzeichnisse auf.",
            );
          }

          const beschriftungen = paragraphsWithStyle(snapshot, "Caption");
          const notizen = await readNotes();
          const felder = await readFields();

          const kriterien = [
            criterion(`Mindestens drei Überschriften (gefunden: ${alle.length})`, alle.length >= 3),
            criterion("Ein Inhaltsverzeichnis ist vorhanden", hasTableOfContents(snapshot)),
            criterion(
              `Mindestens eine Beschriftung (gefunden: ${beschriftungen.length})`,
              beschriftungen.length >= 1,
            ),
          ];

          if (notizen !== null) {
            kriterien.push(
              criterion(
                `Mindestens zwei Fußnoten (gefunden: ${notizen.footnotes.length})`,
                notizen.footnotes.length >= 2,
              ),
            );
          }
          if (felder !== null) {
            const querverweise = felder.filter(
              (feld) => /^\s*(REF|PAGEREF|NOTEREF)\b/i.test(feld.code) || feld.type === "Ref",
            );
            kriterien.push(
              criterion(
                `Mindestens ein Querverweis (gefunden: ${querverweise.length})`,
                querverweise.length >= 1,
              ),
            );
          }

          const nichtPruefbar = [
            notizen === null ? "Fußnoten" : null,
            felder === null ? "Querverweise" : null,
          ].filter((eintrag): eintrag is string => eintrag !== null);

          return fromCriteria(kriterien, {
            success:
              nichtPruefbar.length > 0
                ? `Alles Prüfbare sitzt. Nicht prüfbar in dieser Word-Version: ${nichtPruefbar.join(" und ")}.`
                : "Sehr stark – die Arbeit trägt sich jetzt selbst.",
            failure: "Fast geschafft. Diese Punkte fehlen noch:",
            hint: "Vergiss zum Schluss Strg + A und F9 nicht, damit alle Verzeichnisse den aktuellen Stand zeigen.",
          });
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Alle Verweise in Word funktionieren nach demselben Prinzip: Es sind Felder, die " +
          "auf etwas im Dokument zeigen, statt eine Angabe fest einzutragen. Deshalb " +
          "stimmen sie auch dann noch, wenn sich das Dokument verändert – und deshalb " +
          "müssen sie mit F9 aktualisiert werden.",
        "Wer bis hierhin gekommen ist, kann ein hundertseitiges Dokument umbauen, ohne " +
          "eine einzige Nummer nachzupflegen.",
      ],
      keyPoints: [
        "Alles ist ein Feld: Verzeichnis, Seitenzahl, Querverweis, Zitat.",
        "Strg + A und F9 aktualisiert alle Felder auf einmal.",
        "Beschriftungen sind die Voraussetzung für Abbildungsverzeichnisse.",
        "Vor der Abgabe immer einmal alles aktualisieren und durchsehen.",
      ],
    },
  ],
};
